import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  useToast,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { getRegistryAdmin } from '../services/RegistryService';
import { cancelMealSignup } from '../services/RegistryService';

// Helper function to format date
const formatDate = (dateString: string): string => {
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayOfWeek = date.getUTCDay();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const shortYear = year.toString().slice(2, 4);
  return `${dayNames[dayOfWeek]} ${month}/${day}/${shortYear}`;
};

// Helper function to format time
const formatTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};


const Cancel = () => {
    const { registryId, gifterId } = useParams();
    const [registry, setRegistry] = useState<any>(null);
    const [mealSignup, setMealSignup] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const toast = useToast();
    
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    
    useEffect(() => {
        const fetchRegistry = async () => {
            try {
                if (!registryId || !gifterId) {
                    setError('Missing registry or signup information');
                    setLoading(false);
                    return;
                }
                
                const registryData = await getRegistryAdmin(registryId);
                if (!registryData) {
          setError('Registry not found');
          setLoading(false);
          return;
        }
        
        setRegistry(registryData);
        
        // Find the meal signup with the matching gifterId in currentSignUps
        let matchingMeal = null;
        
        if (registryData.currentSignUps && Array.isArray(registryData.currentSignUps)) {
          matchingMeal = registryData.currentSignUps.find(
            (signup: any) => signup.gifterId === gifterId && signup.isClaimed
          );
        }
        
        if (!matchingMeal) {
          setError('Meal signup not found');
          setLoading(false);
          return;
        }
        
        setMealSignup(matchingMeal);
        setLoading(false);
    } catch (err) {
        console.error('Error fetching registry:', err);
        setError('Failed to load registry information');
        setLoading(false);
    }
};

fetchRegistry();
}, [registryId, gifterId]);

if (loading) {
    return (
      <Container maxW="container.md" py={10} centerContent>
        <Spinner size="xl" color="mamaFuel.moss.500" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
        <Box mt={4}>
          <Link to="/">
            <Button colorScheme="blue">Return Home</Button>
          </Link>
        </Box>
      </Container>
    );
  }

  const handleCancel = async () => {
    try {
      // Check if registryId and gifterId are defined before passing them
      if (!registryId || !gifterId) {
        setError('Missing required information to cancel meal');
        setLoading(false);
        return;
      }
      
      const success = await cancelMealSignup(registryId, gifterId);
      if (success) {
        window.location.href = `/registry/${registryId}`;
        toast({
          title: 'Meal signup cancelled successfully',
          description: 'Your slot is now available for someone else to claim',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setLoading(false);
      } else {
        setError('Failed to cancel meal signup');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error cancelling meal signup:', err);
      setError('Failed to cancel meal signup');
      setLoading(false);
    }
  };
  
  return (
    <Container maxW="container.md" py={10}>
      <Box 
        bg={bgColor} 
        p={6} 
        borderRadius="md" 
        boxShadow="md"
        border="1px" 
        borderColor={borderColor}
      >
        <Heading size="lg" mb={4} color="mamaFuel.moss.700">
          Cancel Meal Signup
        </Heading>
        
        <VStack align="stretch" spacing={4}>
          <Text>
            You're about to cancel your meal signup for <strong>{registry.parentNames}</strong>. 
            Please review the details below:
          </Text>
          
          <Box p={4} bg="gray.50" borderRadius="md">
            <VStack align="stretch" spacing={2}>
              <Text><strong>Date:</strong> {formatDate(mealSignup.date)}</Text>
              <Text>
                <strong>Time:</strong> {formatTime(mealSignup.timeWindow.start)} - {formatTime(mealSignup.timeWindow.end)}
              </Text>
              <Text>
                <strong>Meal:</strong> {mealSignup.actualMealName || mealSignup.name}
              </Text>
              <Text>
                <strong>Signed up as:</strong> {mealSignup.gifterName}
              </Text>
            </VStack>
          </Box>
          
          <Text>
            Are you sure you want to cancel this meal signup? This action cannot be undone.
          </Text>
          
          <Flex justifyContent="space-between" mt={4}>
            <Button 
              variant="outline" 
              colorScheme="gray"
              as={Link}
              to={`/registry/${registryId}`}
            >
              View Registry
            </Button>
            
            <Button 
              variant="red"
              onClick={handleCancel}
              // We'll implement this functionality in the next step
            >
              Cancel My Signup
            </Button>
          </Flex>
        </VStack>
      </Box>
    </Container>
  );
};

export default Cancel;
